/**
 *  Application API module
 * Contains all API functionality to/from the server for the application
 * @module portalapi
 * @author Scott Gingerysty (Fresche Solutions)
 * @date April 2020
 */

import axios from "axios";

import portalStore from "@/store/store";
import qs from "qs";

//axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
//axios.defaults.withCredentials = true;

const API_DATE_FORMAT = "YYYY-MM-DD-HH.mm.ss";
let basePath;
let baseNexusPath;

// Track active controllers
const activeControllers = [];

// if in local mode use proxy server
if (process.env.VUE_APP_ENVIRONMENT === "local") {
  basePath = process.env.VUE_APP_DEV_PROXY + `/api`;
  baseNexusPath - process.env.VUE_APP_NEXUS_PATH;
} else {
  basePath = process.env.VUE_APP_API_PATH + `/api`;
}

let setCommonHeader = (headerName, headerVal) => {
  axios.defaults.headers.common[headerName] = headerVal;
};

// The audit user is used in URL paths for simple logging of actions
// via web server logs - it's not used by the application
let auditUser = "PORTAL_API_NOT_LOGGED_IN";
let setAuditUser = (user) => {
  auditUser = user;
};

let encodeUrlPathSegment = (value) => {
  return encodeURIComponent(value);
};

axios.interceptors.request.use(function (request) {
  // set spinners on if we're using them
  portalStore.state.loadingCount++;
  if (portalStore.state.useLoading) {
    portalStore.state.isLoading = true;
  }

  return request;
});

axios.interceptors.request.use(config => {
  const controller = new AbortController();
  config.signal = controller.signal;
  activeControllers.push(controller);

  const token = sessionStorage.getItem('token');

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  config.headers['Access-Control-Allow-Origin'] = process.env.VUE_APP_DEV_PROXY; // Back-end URL
  config.headers['Accept'] = 'application/json';
  return config;
});

// if any API calls fail with no auth or auth expired redirect user to login
axios.interceptors.response.use(
  function (response) {
    // Remove controller for completed request
    if (activeControllers.length) {
      activeControllers.shift();
    }

    // set spinners off
    portalStore.state.loadingCount--;
    if (portalStore.state.loadingCount === 0) {
      portalStore.state.isLoading = false;
    }
    return response;
  },
  async function (error) {
    console.log(error);

    // Remove controller for failed request
    if (activeControllers.length) {
      activeControllers.shift();
    }

    // Set spinners off
    portalStore.state.loadingCount--;
    if (portalStore.state.loadingCount === 0) {
      portalStore.state.isLoading = false;
    }

    // Skip showing error toast for aborted requests
    if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
      return error;
    }

    // Skip showing error toast for invoice PDF 404 errors (handled in Invoices module)
    if (error.config && error.config.url && error.config.url.includes('/invoices/pdf/') &&
        error.response && error.response.status === 404) {
      return error;
    }

    // Create an error message
    let errMessage = "";
    if (error.response && error.response.data) {
      // If there is an error title and message use both in error response
      if (error.response.data.title && error.response.data.message) {
        errMessage = error.response.data.title + ": " + error.response.data.message;
      } 
      // Else if there is only an error title use it for error response
      else if (error.response.data.title) {
        errMessage = error.response.data.title;
      }
      // Handle validation errors array
      else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
        for (let err of error.response.data.errors) {
          errMessage += `${err.message}\n`;
        }
      }
      // Else if there is only an error message use it for error response
      else if (error.response.data.message) {
        errMessage = error.response.data.message;
      }
    } else {
      // Show the error with a generic default if none was retrieved
      vm.config.globalProperties.$toast.open({
        message: errMessage != "" ? errMessage : "An unexpected error occured.",
        type: "error",
      });
    }

    // Only show error message if it's not an unauthorized error when the user is already logged out
    if (portalStore.state.User.loggedIn || errMessage != "Unauthenticated.") {
      // Show the error with a generic default if none was retrieved
      vm.config.globalProperties.$toast.open({
        message: errMessage != "" ? errMessage : "An unexpected error occured.",
        type: "error",
      });
    }

    // Cancel all outstanding requests on unauthorized and redirect to log in
    if (error.response && (
      error.response.status == vm.config.globalProperties.$constants.HTTP_STATUS_CODES.UNAUTHORIZED ||
      error.response.data.message == "Unauthenticated."
    )) {
      // Cancel active requests
      activeControllers.forEach(controller => controller.abort());
      activeControllers.length = 0; // Clear array

      // Clear Vuex and session storage data
      await portalStore.dispatch("clearSessionStorageData");

      // Redirect to login
      if (window.$router) {
        window.$router.push("/pages/login");
      } else if (vm && vm.config && vm.config.globalProperties && vm.config.globalProperties.$router) {
        vm.config.globalProperties.$router.push("/pages/login");
      } else {
        window.location.href = "/pages/login";
      }
    }

    // Invalid login credentials or too many login attempts
    if (error.response && (error.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.UNPROCESSABLE_CONTENT || error.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.TOO_MANY_ATTEMPTS) && error.response.request.responseURL === `${basePath}/login`) {
      error.data = {
        status: "fail",
      }
      return error;
    }

    return error;
  }
);


let appPortal = {
  async login(loginData) {
    const url = basePath + `/login`;

    return axios.post(url, loginData);
  },

  async fetchUserData() {
    // Fetch current users data
    return axios.get(basePath + `/currentUser`);
  },

  async logout() {
    const url = basePath + `/logout`;

    return axios.post(url);
  },

  /**
   * Submit update password request
   */
  async updatePassword(updateData) {
    return axios.post(`${basePath}/updatePassword`, updateData).catch(error => {});
  },
  
  /**
   * Submit update narcotic code request
   */
  async updateNarcoticCode(updateData) {
    return axios.post(`${basePath}/updateNarcoticCode`, updateData).catch(error => {});
  },

  /**
   * Set user email
   */
  async setUserEmail(updateData) {
    return axios.post(`${basePath}/user/setEmail`, updateData).catch(error => {});
  },

  /**
   * Submit forgot password request
   */
  async forgotPasswordRequest(data) {
    return axios.post(`${basePath}/forgotPassword`, data).catch(error => {});
  },

  async resetPassword(resetData) {
    return axios.post(`${basePath}/resetPassword`, resetData).catch(error => {});
  },
};


let moduleCarts = {
  /**
   * Retrieve cart data
   */
  async getCart(cartType) {
    return axios.get(basePath + `/cart/` + cartType).catch(error => {});
  },

  /**
   * Add items
   */
  async addItemsToCart(payload) {
    return axios.post(`${basePath}/cart/` + payload.cartType, payload).catch(error => {});
  },

  /**
   * Remove item
   */
  async removeItemFromCart(cartType, cartLineId) {
    return axios.delete(`${basePath}/cart/` + cartType + `/${cartLineId}`).catch(error => {});
  },

  /**
   * Empty cart
   */
  async emptyCart(cartType) {
    return axios.delete(`${basePath}/cart/` + cartType + `/empty`).catch(error => {});
  },
  
  /**
   * Update line item quantity
   */
  async updateCartItemQuantity(payload) {
    return axios.post(`${basePath}/cart/` + payload.cartType + `/updateQuantity`, payload).catch(error => {});
  },

  /**
  * Update line item added text
  */
  async updateCartItemAddedText(payload) {
    return axios.post(`${basePath}/cart/` + payload.cartType + `/updateItemText`, payload).catch(error => {});
  },

  /**
   * Update line item reason code
   */
  async updateClaimCartItemReasonCode(payload) {
    return axios.post(`${basePath}/cart/claim/updateItemReasonCode`, payload).catch(error => {});
  },

  /**
   * Update line item invoice number
   */
  async updateClaimCartItemInvoiceNumber(payload) {
    return axios.post(`${basePath}/cart/claim/updateItemInvoiceNumber`, payload);
  },

  /**
   * Update number of boxes
   */
  async updateClaimCartNumberOfBoxes(payload) {
    return axios.post(`${basePath}/cart/claim/updateNumberOfBoxes`, payload).catch(error => {});
  },

  /**
   * Update line item expiry date
   */
  async updateClaimCartItemExpiryDate(payload) {
    return axios.post(`${basePath}/cart/claim/updateItemExpiryDate`, payload).catch(error => {});
  },

  /**
   * Update purchase order number
   */
  async updateCartPurchaseOrderNumber(payload) {
    return axios.post(`${basePath}/cart/` + payload.cartType + `/updatePoNumber`, payload).catch(error => {});
  },

  /**
   * Update header text
   */
  async updateCartHeaderText(payload) {
    return axios.post(`${basePath}/cart/` + payload.cartType + `/updateHeaderText`, payload).catch(error => {});
  },

  /**
   * Consolidate duplicate items
   */
  async consolidateCartItems(payload) {
    let url = basePath + `/cart/` + payload.cartType + `/consolidate/` + payload.itemNumber;

    // When consolidating claim items, also pass the invoice number
    if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
      url += `/` + payload.invoiceNumber;
      url += `/` + payload.reasonCode;
      url += `/` + payload.isReturn;
    }
    
    return axios.post(url).catch(error => {});
  },

  /**
   * Submit cart
   */
  async submitCart(cartType, payload) {
    return axios.post(basePath + `/cart/` + cartType + `/submit/`, payload).catch(error => {});
  },

  /**
   * Get cart validation
   */
  async getCartValidation(cartType) {
    return axios.post(basePath + `/cart/` + cartType + `/getCartAdjustments/`).catch(error => {});
  },

  /**
   * Add regular cart items to store cart
   */
  async addRegularCartItemsToStoreCart() {
    return axios.post(basePath + `/cart/regular/addToStoreCart`).catch(error => {});
  },
};
  

let moduleCatalogs = {
  /**
   * Regular Catalog
   */
  async getRegularCatalogData(data) {
    // Make request and expect response with an array of regular catalog items.
    return axios.post(basePath + `/catalog/regular?page=` + data.page, data).catch(error => {});
  },

  /*
   * Item Search
   */
  async getSearchItems(data) {
    return axios.post(basePath + `/catalog/itemSearch?page=` + data.page, data).catch(error => {});
  },

  /**
   * Get Item Change Log Data
   */
  async getItemChanges(data) {
    return axios.post(basePath + `/catalog/itemChangeLog?page=` + data.page, data).catch(error => {});
  },

  /**
   * Retrieve Item Change Types
   */
  async getItemChangeTypes() {
    // Make request and expect response with an array of item change types.
    return axios.get(basePath + `/lookUp/itemChangeTypes`).catch(error => {});
  },
  
  /**
   * Get New Items
   */
  async getNewItems(data) {
    return axios.post(basePath + `/catalog/newItems?page=` + data.page, data).catch(error => {});
  },
};


let moduleItem = {
  /**
   * Retrieve class codes from CSV file
   */
  async getClassCodes() {
    // Make request and expect response with an array of item classes.
    return axios.get(basePath + `/lookUp/itemClasses`).catch(error => {});
  },

  /**
   * Retrieve vendor codes
   */
  async getVendorCodes() {
    // Make request and expect response with an array of vendor codes.
    return axios.get(basePath + `/lookUp/vendors`).catch(error => {});
  },

  /**
   * Retrieve MAI Short Codes codes from API
   */
  async getMaiShortCodes() {
    // Make request and expect response with an array of main short codes.
    return axios.get(basePath + `/lookUp/mainShortCodes`).catch(error => {});
  },

  /**
   * Retrieve Item Groups from API
   */
  async getItemGroups() {
    // Make request and expect response with an array of item groups.
    return axios.get(basePath + `/lookUp/itemGroups`).catch(error => {});
  },

  /**
   * Retrieve Unipharm Item Groups from API
   */
  async getUnipharmItemGroups() {
    // Make request and expect response with an array of Unipharm item groups.
    return axios.get(basePath + `/lookUp/unipharmItemGroups`).catch(error => {});
  },

  /**
   * Get Data for all active items
   */
  async getAllActiveItems() {
    // Make request and expect response with an array of item objects.
    return axios.get(basePath + `/catalog/items`).catch(error => {});
  },

  // TODO: To be implemented post go live
  // /** 
  //  * Get Users top items data
  //  */
  // async getUsersTopItemsData(data) {
  //   // TODO - implement API call and remove dummy data retrieval below:
  //   var jsonData = require('./TempJsonData/Items.json');
  //   return jsonData.itemDetails.filter(item => item.itemGroup == data);
  // },

  /**
   * Get Item Lookup data
   */
  async getItemLookupData() {
    // Make request and expect response with an array of item lookup dataobjects.
    return axios.get(basePath + `/lookUp/items`).catch(error => {});
  },

  /**
   * Get item data for basic listing
   */
  async getItemListingData(data) {
    return axios.post(basePath + `/items/basicListingData`, data).catch(error => {});
  }, 

  /**
   * Retrieve Item Details
   */
  async getItemDetails(itemNumber) {
    return axios.get(basePath + `/items/` + itemNumber).catch(error => {});
  },
};


let moduleWebUsers = {
  /**
   * Web Users
   */
  async getWebUsers(data) {
    // Make request and expect response with an array of user objects.
    return axios.post(basePath + `/user/all?page=` + data.page, data).catch(error => {});
  },

  async createWebUser(data) {
    // Create the user and expect a successful response
    return axios.put(basePath + `/user`, data).catch(error => {});
  },

  async updateWebUser(data) {
    // Update the user and expect a successful response
    return axios.post(basePath + `/user/` + encodeUrlPathSegment(data.data.userId), data).catch(error => {});
  },

  async deleteWebUser(data) {
    // Delete the user and expect a successful response
    return axios.post(`${basePath}/user/delete/` + encodeUrlPathSegment(data.userId), data).catch(error => {});
  },

  /**
   * Selected User
   */
  async getSelectedUserData(payload) {
    // Make request and expect response with a user object.
    return axios.get(basePath + `/user/` + encodeUrlPathSegment(payload.storeId) + `/` + encodeUrlPathSegment(payload.userId)).catch(error => {});
  },

  /**
   * User Roles
   */
  async getUserRoles() {
    // Make request and expect response with an array of user roles.
    return axios.get(basePath + `/codes/userRoles`).catch(error => {});
  },

  /**
   * Set user's default show legend preference
   */
  async setUserShowLegendPreference(data) {
    return axios.post(basePath + `/setShowLegend`, data).catch(error => {});
  },

  /**
   * Set user's default page size preference
   */
  async setUserPageSizePreference(data) {
    return axios.post(basePath + `/setPageSize`, data).catch(error => {});
  },
};


/**
 * Requests specific to Super/Unipharm Admins
 */
let moduleAdmin = {
  /**
   * Web Users
   */
  async getAllUsersPage(data) {
    // Make request and expect response with an array of user objects.
    return axios.post(basePath + `/admin/getUsers?page=` + data.page, data).catch(error => {});
  },

  /**
   * Reset users Narcotic Control Code
   */
  async resetNarcoticCode(data) {
    // Reset the users narcotic code and expect a successful response
    return axios.post(basePath + `/admin/resetNcc`, data).catch(error => {});
  },

  /**
   * Toggle users Super Admin Authority
   */
  async toggleSuperAdminAuthority(data) {
    // Toggle the users Super Admin Authority and expect a successful response
    return axios.post(basePath + `/admin/toggleSuperAdmin`, data).catch(error => {});
  },

  /**
   * Get list of all pharmacies
   */
  async getAllPharmacies() {
    // Make request and expect response with an array of pharmacy id's and name's.
    return axios.get(basePath + `/admin/pharmacies`).catch(error => {});
  },

  /**
   * Get list of all pharmacies in D365 but not in Web Orders
   */
  async getAllUnregisteredPharmacies() {
    // Make request and expect response with an array of pharmacy id's and name's.
    return axios.get(basePath + `/admin/unregisteredPharmacies`).catch(error => {});
  },

  /**
   * Create new client pharmacy settings record
   */
  async createClientPharmacy(accountNumber) {
    // Create the client pharmacy settings and expect a successful response
    return axios.put(basePath + `/admin/clientPharmacy/` + accountNumber).catch(error => {});
  },
};


let moduleClientPharmacy = {
  /**
   * Site Settings
   */
  async updateSiteSettings(id, data) {
    // Update the site settings and expect a successful response
    return axios.put(basePath + `/clientPharmacy/` + id, data).catch(error => {});
  },

  async getClientPharmacy(id) {
    // Make request for site settings and expect response with a client pharmacy object.
    return axios.get(basePath + `/clientPharmacy/` + id).catch(error => {});
  },

  /**
   * Customer Groups
   */
  async getCustomerGroups() {
    // Make request and expect response with an array of customer groups.
    return axios.get(basePath + `/customerGroups/`).catch(error => {});
  },

  async getDailyOrderTotal() {
    return axios.get(basePath + `/clientPharmacy/dailyTotalOrders`).catch(error => {});
  }
};


let moduleNarcoticControlForms = {
  /**
   * Narcotic Forms
   */
  async getNarcoticControlForm(formNumber) {
    return axios.get(basePath + `/ncf/` + formNumber).catch(error => {});
  },

  /**
   * Submit Narcotic Form
   */
  async submitNcfToD365(data) {
    return axios.post(basePath + `/ncf/signNcf`, data).catch(error => {});
  },

  /**
   * Submit Narcotic Form Dispute
   */
  async submitDisputeToD365(data) {
    return axios.post(basePath + `/ncf/disputeNcf`, data).catch(error => {});
  },
  
  /**
   * Get Narcotic Form Page
   */
  async getNarcoticFormsPage(data) {
    return axios.post(basePath + `/ncf?page=` + data.page, data).catch(error => {});
  },

  /**
   * Get Narcotic Form Status Codes
   */
  async getNarcoticFormStatusCodes() {
    return axios.get(basePath + `/lookUp/narcoticFormStatusCodes`).catch(error => {});
  },
};


let moduleItemLabels = {
  /**
   * Item Labels
   */
  async getItemLabelListData(data) {
    // Make request and expect response with an array of item label data.
    return axios.post(basePath + `/catalog/itemLabels?page=` + data.page, data).catch(error => {});
  },
  
  async getItemLabelCartData() {
    // Make request and expect response with an array of item label data.
    return axios.get(basePath + `/cart/printLabel`).catch(error => {});
  },

  async getPriceTicketPrintOptions() {
    return axios.get(basePath + `/lookUp/priceTicketPrintOptions`).catch(error => {});
  },

  async getShelfLabelPrintOptions() {
    return axios.get(basePath + `/lookUp/shelfLabelPrintOptions`).catch(error => {});
  },

  async getPrintLabelSequenceOptions() {
    return axios.get(basePath + `/lookUp/printLabelSequenceOptions`).catch(error => {});
  },
};


let moduleInvoices = {
  /**
   * Invoices
   */
  async getInvoice(invoiceNumber) {
    // Make request and expect response with invoice objects.
    return axios.get(basePath + `/invoices/` + invoiceNumber).catch(error => {});
  },
  
  async getInvoicesPage(data) {
    // Make request and expect response with invoice objects.
    return axios.post(basePath + `/invoices?page=` + data.page, data).catch(error => {});
  },

  async addInvoiceItemsToLabelCart(invoiceNumber) {
    // Make request and expect response status
    return axios.post(basePath + `/invoices/addToPrintLabelQueue/` + invoiceNumber).catch(error => {});
  },

  /**
   * Retrieve List of Invoices limited to logged in user's store
   */
  async getStoreInvoiceNumbers() {
    // Make request and expect response with an array of invoice numbers for store of logged in user.
    return axios.get(basePath + `/lookUp/storeInvoiceNumbers`).catch(error => {});
  },  

  async getInvoiceTypes() {
    return axios.get(basePath + `/lookUp/invoiceTypes`).catch(error => {});
  },

  async getInvoicePdf(invoiceNumber) {
    return axios.get(basePath + `/invoices/pdf/` + invoiceNumber, {responseType: 'arraybuffer'}).catch(error => {});
  },
};


let moduleSavedItems = {
  async getSavedItemsLists(data) {
    return axios.post(basePath + `/savedItemsLists?page=` + data.page, data).catch(error => {});
  },

  async deleteSavedItemsList(listId) {
    return axios.delete(basePath + `/savedItemsLists/` + listId).catch(error => {});
  },

  async createSavedItemsList(data) {
    return axios.put(basePath + `/savedItemsLists`, data).catch(error => {});
  },

  async getSelectedListData(listId) {
    return axios.get(basePath + `/savedItemsLists/` + listId).catch(error => {});
  },

  async updateListData(data) {
    return axios.post(basePath + `/savedItemsLists/` + data.listId, data).catch(error => {});
  },

  async addListItemsToCart(payload) {
    return axios.post(basePath + `/savedItemsLists/addToCart`, payload).catch(error => {});
  },

  async getCartItemData(cartType) {
    if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
      return axios.get(basePath + `/savedItemsLists/regularCartItems`).catch(error => {});
    } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE) {
      return axios.get(basePath + `/savedItemsLists/storeCartItems`).catch(error => {});
    }
  }
};


let moduleOrders = {
  /**
   * Orders
   */
  async getOrder(salesId) {
    return axios.get(basePath + `/orders/${salesId}`).catch(error => {});
  },

  async getAllOrders(data) {
    return axios.post(basePath + `/orders?page=` + data.page, data).catch(error => {});
  },

  async getSalesOrderTypes() {
    return axios.get(basePath + `/lookUp/salesOrderTypes`).catch(error => {});
  },

  async getClaimReturnCodes() {
    return axios.get(basePath + `/lookUp/claimReturnCodes`).catch(error => {});
  },

  async getSalesOrderStatus() {
    return axios.get(basePath + `/lookUp/salesOrderStatus`).catch(error => {});
  },

  async getOrderConfirmation(salesId) {
    return axios.get(basePath + `/orders/orderConfirmation/${salesId}`).catch(error => {});
  }
};


let moduleUserNotifications = {
  /**
   * User Notifications
   */
  async getUserNotifications(page) {
    // Make request and expect response with an array of user notifications.
    return axios.post(basePath + `/notifications?page=` + page).catch(error => {});
  },

  /**
   * Delete Notification
   */
  async deleteNotification(id) {
    return axios.delete(basePath + `/notifications/${id}`).catch(error => {});
  },

  /**
   * Mark notification as seen
   */
  async markNotificationAsSeen(id) {
    return axios.post(basePath + `/notifications/${id}/markSeen`).catch(error => {});
  },

  /**
   * Check for applicable User Notifications then create them
   */
  async checkUserLoginNotifications() {
    // Make request and expect response with an array of user roles.
    return axios.post(basePath + `/notifications/create`).catch(error => {});
  },

  /**
   * Clear all user notifications
   */
  async clearAllUserNotifications() {
    return axios.delete(basePath + `/notifications/clearAll`).catch(error => {});
  },
};


let moduleReports = {
  // TODO: To be implemented post go live
  // /**
  //  * Get Summary Purchases Report
  //  */
  // async getMonthlyPurchasesSummary() {
  //   // TODO - implement API call to retrieve Monthly Purchases Report
  //   let jsonData = require('./TempJsonData/MonthlyPurchases.json');
  //   let data =  {'details': jsonData.summary, 'total': jsonData.totalSummary};
  //   return data;
  // },

  // TODO: To be implemented post go live
  // /**
  //  * Get Monthly Purchases Report
  //  */
  // async getMontlyPurchases(month, year) {
  //   // TODO - implement API call to retrieve Monthly Purchases Report
  //   var jsonData = require('./TempJsonData/MonthlyPurchases.json');

  //   return jsonData.monthly.filter(report => report.month == month && report.year == year);
  // },

  // TODO: To be implemented post go live
  // /**
  //  * Get Monthly Purchases Report by Account Group
  //  */
  // async getMontlyPurchasesByAccountGroup(month, year, accountGroup) {
  //   // TODO - implement API call to retrieve Monthly Purchases Report
  //   var jsonData = require('./TempJsonData/MonthlyPurchasesAccountGroup.json');

  //   return jsonData.accountGroup.filter(report => report.month == month &&  report.year == year && report.accountGroup == accountGroup);
  // },

  // TODO: To be implemented post go live
  // /**
  //  * Get Monthly Purchases Report by  Account and Item Group
  //  */
  // async getMontlyPurchasesByItemGroup(month, year, accountGroup, itemGroup) {
  //   // TODO - implement API call to retrieve Monthly Purchases Report
  //   var jsonData = require('./TempJsonData/MonthlyPurchasesAccountGroup.json');
    
  //   return jsonData.itemGroup.filter(report => report.month == month && report.year == year && report.accountGroup == accountGroup && report.itemGroup == itemGroup);
  // },
};


let moduleResources = {
  /**
   * All Resources
   */
  async getResourcesPage(data) {
    // Make request and expect response with an array of resource objects.
    return axios.post(basePath + `/resources?page=` + data.page, data).catch(error => {});
  },

  /**
   * All Resource Types (admin paginated list)
   */
  async getResourceTypesPage(data) {
    return axios.post(basePath + `/resourceTypes?page=` + data.page, data).catch(error => {});
  },

  /**
   * Selected Resource Type
   */
  async getSelectedResourceTypeData(resourceTypeId) {
    return axios.get(basePath + `/resourceTypes/` + resourceTypeId).catch(error => {});
  },

  /**
   * Create Resource Type
   */
  async createResourceType(data) {
    return axios.put(basePath + `/resourceTypes/`, data).catch(error => {});
  },

  /**
   * Update Resource Type
   */
  async updateResourceType(data) {
    return axios.post(basePath + `/resourceTypes/` + data.data.id, data).catch(error => {});
  },

  /**
   * Delete Resource Type
   */
  async deleteResourceType(id) {
    return axios.delete(basePath + `/resourceTypes/` + id).catch(error => {});
  },

  /**
   * Toggle a resource type's active status
   */
  async toggleResourceTypeActive(id) {
    return axios.post(basePath + `/resourceTypes/toggleActive/` + id).catch(error => {});
  },

  /**
   * Update a resource type's authorities (sync: replaces all existing)
   */
  async updateResourceTypeAuthorities(id, customerGroups) {
    return axios.post(basePath + `/resourceTypes/authorities/` + id, { customerGroups }).catch(error => {});
  },

  /**
   * Selected Resource
   */
  async getSelectedResourceData(resourceId) {
    // Make request and expect response with a resource object.
    return axios.get(basePath + `/resources/` + resourceId).catch(error => {});
  },

  /**
   * Create Resource
   */
  async createResource(data) {
    // Create the resource and expect a successful response
    return axios.put(basePath + `/resources/`, data).catch(error => {});
  },

  /**
   * Update Resource
   */
  async updateResource(data) {
    // Update the resource and expect a successful response
    return axios.post(basePath + `/resources/` + data.data.id, data).catch(error => {});
  },

  /**
   * Delete Resource
   */
  async deleteResource(id) {
    // Delete the resource and expect a successful response
    return axios.delete(basePath + `/resources/` + id).catch(error => {});
  },

  /**
   * Expire a resource
   */
  async toggleResourceExpiry(id) {
    // Set resource expiry date
    return axios.post(basePath + `/resources/toggleExpired/` + id).catch(error => {});
  },

  /**
   * Toggle a resource's priority
   */
  async togglePriority(id) {
    // Toggle priority flag for a resource
    return axios.post(basePath + `/resources/togglePriority/` + id).catch(error => {});
  },

  /**
   * Retrieves a set of resources based on resource type
   */
  async getResourcesByType(type, data) {
    // Make request and expect response with an array of resource objects.
    return axios.post(basePath + `/resources/listing/` + type, data).catch(error => {});
  },

  /**
   * Retrieves a resource file
   */
  async getResourceFile(resourceId) {
    // Make request and expect response with file.
    return axios.get(basePath + `/resources/file/` + resourceId, {responseType: 'arraybuffer'}).catch(error => {});
  },

  /**
   * Retrieves all active resource types with their display configuration
   */
  async getResourceTypes() {
    return axios.get(basePath + `/resources/types`).catch(error => {});
  },

  /**
   * Retrieves all resource types (active and inactive) as id/description/isActive
   * for use in admin form dropdowns.
   */
  async getResourceTypesForAdmin() {
    return axios.get(basePath + `/resourceTypes/allForAdmin`).catch(error => {});
  },

  /**
   * Retrieves all distinct resource group names (active and inactive types)
   */
  async getResourceGroups() {
    return axios.get(basePath + `/resourceTypes/groups`).catch(error => {});
  },

  /**
   * Retrieves all resource types with display order data (id, description, resourceGroup, displayOrder)
   */
  async getResourceTypesDisplayOrder() {
    return axios.get(basePath + `/resourceTypes/displayOrder`).catch(error => {});
  },

  /**
   * Bulk-updates the displayOrder for a set of resource types.
   * @param {Array<{ id: number, displayOrder: number }>} order
   */
  async updateResourceTypeDisplayOrder(order) {
    return axios.post(basePath + `/resourceTypes/updateDisplayOrder`, { order }).catch(error => {});
  },
};


let moduleStatements = {
  /**
   * Statements
   */
  async getStatementsPage(data) {
    return axios.post(basePath + `/statements?page=` + data.page, data).catch(error => {});
  },

  async getStatement(statementId, statementType) {
    return axios.get(basePath + `/statements/` + statementType + `/` + statementId).catch(error => {});
  },

  async getStatementTypes() {
    return axios.get(basePath + `/lookUp/statementTypes`).catch(error => {});
  }
};


let moduleUploadHandHeldFile = {
    /**
     * Upload orders to cart
     */
    async uploadOrdersToCart(cartType, payloadData, labelType = null) {
      let endpoint = `${basePath}/uploadHandHeldFile/`;

      if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
        endpoint += "regularCart";
      } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_LABEL) {
        endpoint += "printLabelQueue/" + labelType;
      } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
        endpoint += "claimCart";
      }

      return axios.post(endpoint, payloadData, {
        headers: {
          "Content-Type": "multipart/form-data",
        }
      }).catch(error => {});
    },
};


let moduleSrps = {
  /**
   * SRP Classes
   */
  async getSrpClasses(data) {
    // Make request and expect response with an array of SRP Class objects.
    return axios.post(basePath + `/srps/classes?page=` + data.page, data).catch(error => {});
  },

  /**
   * SRP Class Items and Data
   */
  async getSrpClassItems(data) {
    // Make request and expect response with an array of SRP Class items.
    return axios.get(basePath + `/srps/class/` + data).catch(error => {});
  },

  /**
   * Invoice SRP Items and Data
   */
  async getInvoiceSrpItems(data) {
    // Make request and expect response with an array of invoice SRP items.
    return axios.get(basePath + `/srps/invoice/` + data).catch(error => {});
  },

  /**
   * Update SRPs
   */
  async updateSrps(data) {
    // Update the SRP and expect a successful response
    return axios.post(basePath + `/srps/update`, data).catch(error => {});
  },
};


export default {
  setCommonHeader,
  setAuditUser,
  API_DATE_FORMAT,
  appPortal,
  moduleCarts,
  moduleItem,
  moduleWebUsers,
  moduleClientPharmacy,
  moduleCatalogs,
  moduleNarcoticControlForms,
  moduleItemLabels,
  moduleInvoices,
  moduleSavedItems,
  moduleOrders,
  moduleAdmin,
  moduleResources,
  moduleReports,
  moduleStatements,
  moduleUploadHandHeldFile,
  moduleUserNotifications,
  moduleSrps,
};
